import React, { useMemo } from "react";
import {
  Button,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableVirtualizedBody,
  Text,
  Tooltip,
} from "@vibe/core";
import { DropdownChevronDown } from "@vibe/icons";
import { flattenRows, type RenderRow } from "./flattenRows";
import StateIndicator, { STATE_VISUALS } from "./stateIndicator";
import type {
  DiscrepancyGridAction,
  DiscrepancyGridProps,
  DisplayValue,
} from "./discrepancyGrid.interface";

/**
 * Row count past which the windowed body engages, and the height of the box it
 * scrolls in. THE POLICY FOR EVERY CONSUMER OF THIS GRID, deliberately owned
 * here rather than passed in.
 *
 * Both numbers used to be per-call-site: the Subitems tab passed 48 and 720,
 * Shared Items passed nothing and got 100 and 600. That is the same grid, one
 * tab click apart, behaving differently for no reason a user could discover —
 * and a value a caller must remember to pass is uniform only until the next
 * call site is added. The Subitems tab's numbers won because they were the
 * reasoned ones; they are now simply the defaults, and the props remain for a
 * caller with a genuinely different container.
 *
 * WHY 48, and not "as soon as it scrolls": react-window keeps only the visible
 * window in the DOM, so browser find-in-page and screen readers stop seeing the
 * rest. Paying that to save mounting a handful of off-screen rows is a bad
 * trade. 48 is four viewports' worth at this height — where the DOM cost is
 * real rather than theoretical — while still being under half of the 100 this
 * grid used to default to, so the long grids a wide board produces (40 subitems
 * x 6 boards is 120 rows on one expand click) are covered.
 *
 * The threshold is measured against FILTERED rows (see `shouldVirtualize`), so
 * a search that narrows 200 rows to 3 correctly renders them whole. Keep that
 * order when the search box lands.
 *
 * 720 is 12 rows at the 60px row height the lists around this grid use.
 */
export const VIRTUALIZE_THRESHOLD = 48;
export const DEFAULT_VIRTUALIZED_HEIGHT = 720;

/**
 * Pins the trailing action column to the right edge. Vibe's Table `sticky` prop is
 * left-only (`position: sticky; left: 0`), so it cannot express this — the rule lives in
 * src/index.css alongside the app's other Vibe adjustments.
 */
const STICKY_END_CLASS = "discrepancy-grid-sticky-end";

/**
 * Scopes the grid-template override in src/index.css to this grid. See GRID_TEMPLATE below.
 */
const GRID_SCOPE_CLASS = "discrepancy-grid-scope";

/**
 * The two pinned edge columns. Fixed rather than fractional — see the `columns` memo below
 * for why. Status is the wider of the two because it holds a state pill AND an action button
 * side by side; the label column only holds a column title.
 *
 * Status is 230px, not the 210px it started at, because the Items tab puts THREE controls in
 * this cell where the Subitems tab puts one: sharedColumnsAdapter gives every row a "Sync
 * Latest" button AND a "View History" icon button, unconditionally. Vibe's TableCell spends
 * 16px of padding per side on top of that, so 210px only ever offered 178px of usable width.
 *
 * 230px is sized for a 22px icon-only StateIndicator (≈208px of content) with headroom, not
 * for a labelled pill — a pill reading "Not managed" beside both controls needs ≈270px. If the
 * labels ever come back, this number has to come back with them.
 */
// `as const` is load-bearing: Vibe types a column width as the template-literal union
// `number | ${number}% | ${number}px | ${number}fr`, which a widened `string` does not satisfy.
const LABEL_COLUMN_WIDTH = "200px" as const;
const STATUS_COLUMN_WIDTH = "230px" as const;

/**
 * The floor under a board column. This is the value that makes the grid genuinely overflow,
 * and it is why the sticky edges work at all.
 *
 * Vibe sizes both `.tableHeader` and `.tableRow` as `width: fit-content; min-width: 100%`.
 * With any `fr` track, that pins the grid's width to its container in both directions:
 * `fit-content` resolves to `available` when content is wide, and `min-width: 100%` stretches
 * it back to `available` when content is narrow. So a grid of `fr` board columns can never be
 * wider than the panel — nothing ever overflows `.table`'s `overflow: auto`, no scrollport
 * exists, and `position: sticky` on the two edge columns does exactly nothing. The board
 * columns compressed instead, and `.tableCell { overflow: hidden }` clipped what no longer
 * fit: six boards on a 1200px panel left each value 99px after cell padding.
 *
 * `minmax()` is the fix, and it cannot go through Vibe's `columns` prop — that prop's width is
 * typed as a single length, which is why GRID_TEMPLATE below is applied through CSS instead.
 */
const BOARD_COLUMN_MIN_WIDTH = "170px";

const renderDefaultValue = (value: DisplayValue | null) => {
  if (value === null) {
    return (
      <Text type="text2" color="secondary">
        —
      </Text>
    );
  }
  if (!value.exists) {
    return (
      <Tooltip content="Column does not exist on this board">
        <Text type="text2" color="secondary">
          Does not exist
        </Text>
      </Tooltip>
    );
  }
  const text = value.text?.trim() ? value.text : "Empty";
  return (
    <Tooltip content={text}>
      <Text type="text2" ellipsis>
        {text}
      </Text>
    </Tooltip>
  );
};

const renderAction = (action: DiscrepancyGridAction) =>
  action.kind === "icon" && action.icon ? (
    <IconButton
      key={action.key}
      // The grid interface intentionally types `icon` as `React.ComponentType<any>`
      // (see discrepancyGrid.interface.ts) rather than Vibe's own `SubIcon`, so
      // callers don't need a Vibe import just to satisfy this field. Cast at the
      // one place it meets a real Vibe component.
      icon={action.icon as unknown as React.FC<any>}
      size="small"
      disabled={action.disabled}
      tooltipContent={action.label}
      ariaLabel={action.label}
      onClick={action.onClick}
    />
  ) : (
    <Button
      key={action.key}
      size="small"
      disabled={action.disabled}
      ariaLabel={action.ariaLabel ?? action.label}
      onClick={action.onClick}
    >
      {action.label}
    </Button>
  );

/** Renders one entry's worth of cells — a data row or a group header. Extracted
 * so both the plain and virtualized bodies invoke this same per-entry rendering;
 * two copies of row markup would drift. The component below wraps this in a
 * `renderEntry(entry)` closure over `boards`/`renderValue` so both bodies call
 * it with the single-argument signature `TableVirtualizedBody`'s `rowRenderer`
 * requires. */
const renderEntryRow = (
  entry: RenderRow,
  boards: DiscrepancyGridProps["boards"],
  renderValue: DiscrepancyGridProps["renderValue"],
  onToggleGroup?: DiscrepancyGridProps["onToggleGroup"],
) => {
  if (entry.kind === "group") {
    const visual = entry.state ? STATE_VISUALS[entry.state] : undefined;
    const toggleable = Boolean(onToggleGroup);

    // TableCell has no `style` prop (only className / sticky / id / data-testid),
    // so a group header cannot span via gridColumn on the cell. TableRow DOES
    // take `style`, and Table lays rows out as CSS grid — so collapse this row
    // to a single track instead.
    return (
      <TableRow key={entry.id} style={{ gridTemplateColumns: "1fr" }}>
        <TableCell sticky>
          {/* One line, deliberately. TableVirtualizedBody wraps react-window's
              FixedSizeList, which gives every row an identical height — a header
              taller than a data row is clipped rather than accommodated, and Vibe
              exports no VariableSizeList to switch to.

              Everything sits in ONE left-aligned cluster with nothing pushed right:
              the board columns scroll horizontally, so "right" is off-screen the
              moment there are enough boards to overflow. */}
          <div
            data-testid="discrepancy-grid-group-header"
            role={toggleable ? "button" : undefined}
            tabIndex={toggleable ? 0 : undefined}
            aria-expanded={toggleable ? !entry.collapsed : undefined}
            onClick={toggleable ? () => onToggleGroup?.(entry.groupId) : undefined}
            onKeyDown={
              toggleable
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onToggleGroup?.(entry.groupId);
                    }
                  }
                : undefined
            }
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-small)",
              cursor: toggleable ? "pointer" : undefined,
            }}
          >
            {toggleable ? (
              <span
                style={{
                  display: "inline-flex",
                  flex: "none",
                  color: "var(--secondary-text-color)",
                  transform: entry.collapsed ? "rotate(-90deg)" : "rotate(0deg)",
                  transition: "transform 0.15s ease",
                }}
              >
                <DropdownChevronDown size={16} />
              </span>
            ) : null}
            <Text type="text2" weight="medium">
              {entry.label}
            </Text>
            {/* On the band, not only on the rows: a collapsed group shows no rows at all, so
                without this a shut list of subitems would display no states and read as a
                clean one. */}
            {visual ? <StateIndicator state={entry.state!} /> : null}
            {entry.summary ? (
              <Text type="text2" color="secondary">
                {entry.summary}
              </Text>
            ) : null}
            {(entry.actions ?? []).map(renderAction)}
          </div>
        </TableCell>
      </TableRow>
    );
  }

  // Built as one array literal, rather than interleaved JSX siblings, because
  // TableRow types `children` as `ReactElement<TableCellProps> |
  // ReactElement<TableCellProps>[]` — a single TableCell, an array from
  // `boards.map`, and another single TableCell as three separate JSX children
  // do not typecheck against that union, even though each individual piece
  // does.
  const cells = [
    // Both edge cells stay pinned while the board columns scroll: a sync group spanning six
    // boards is wider than the panel, and without this the reader loses the column name and
    // the row's actions exactly when they need them. `sticky` also keeps the body's leading
    // cell in step with the already-sticky "Column" header.
    <TableCell key="label" sticky data-testid="discrepancy-grid-label-cell">
      <Text type="text2" weight="medium">
        {entry.row.label}
      </Text>
    </TableCell>,
    ...boards.map((board) => {
      const value = entry.row.valuesByBoard[board.boardId] ?? null;
      return (
        <TableCell key={board.boardId}>
          {renderValue ? renderValue(value, entry.row, board.boardId) : renderDefaultValue(value)}
        </TableCell>
      );
    }),
    // Vibe's own `sticky` prop hardcodes `left: 0`, so it cannot pin a trailing column —
    // hence the local class (see STICKY_END_CLASS).
    <TableCell key="status" className={STICKY_END_CLASS} data-testid="discrepancy-grid-status-cell">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-small)" }}>
        <StateIndicator state={entry.row.state} />
        {(entry.row.actions ?? []).map(renderAction)}
      </div>
    </TableCell>,
  ];

  return <TableRow key={entry.id}>{cells}</TableRow>;
};

const DiscrepancyGrid: React.FC<DiscrepancyGridProps> = ({
  boards,
  rows,
  filter,
  isLoading = false,
  emptyMessage = "No items to display.",
  virtualizeThreshold,
  virtualizedHeight = DEFAULT_VIRTUALIZED_HEIGHT,
  collapsedGroups,
  onToggleGroup,
  renderValue,
}) => {
  const renderRows = useMemo(
    () => flattenRows(rows, filter, collapsedGroups),
    [rows, filter, collapsedGroups],
  );
  const shouldVirtualize = renderRows.length > (virtualizeThreshold ?? VIRTUALIZE_THRESHOLD);
  const renderEntry = (entry: RenderRow) =>
    renderEntryRow(entry, boards, renderValue, onToggleGroup);

  // Both edge columns are FIXED, not fractional. Vibe's Table lays every TableRow out as its
  // own grid, and a bare `1fr` is really `minmax(auto, 1fr)` — the `auto` floor lets a row's
  // own content widen its track. Rows carrying an action button ("Sync Latest", "Create",
  // "Unlink") therefore sized the Status track to that button while `ok` rows, which carry no
  // action, did not, and the header matched neither: the column visibly staggered down the
  // grid. Fixed widths make alignment structural rather than negotiated, and they are also
  // what lets both edges stay pinned while the board columns scroll — a sticky column whose
  // width changes per row jitters as it scrolls.
  const columns = useMemo(
    () => [
      { id: "label", title: "Column", width: LABEL_COLUMN_WIDTH },
      ...boards.map((board) => ({
        id: board.boardId,
        title: board.boardName,
        width: "1.5fr" as const,
      })),
      { id: "status", title: "Status", width: STATUS_COLUMN_WIDTH },
    ],
    [boards],
  );

  /**
   * The real column template, replacing the one Vibe derives from `columns` above.
   *
   * Vibe sets `--table-grid-template-columns` as an INLINE style on `.table`, and both
   * `.tableHeader` and `.tableRow` read it. Redefining that property on the two `rowgroup`
   * elements (see src/index.css) beats the inline value for everything below them, because a
   * custom property redefined lower in the tree wins over an ancestor's — no `!important` and
   * no hashed Vibe class name in our selectors.
   *
   * Built from the same constants the `columns` prop uses, so the two cannot drift: only the
   * board track differs, gaining the `minmax()` floor that the prop's type cannot express.
   */
  const gridTemplate = useMemo(
    () =>
      `${LABEL_COLUMN_WIDTH} repeat(${boards.length}, minmax(${BOARD_COLUMN_MIN_WIDTH}, 1.5fr)) ${STATUS_COLUMN_WIDTH}`,
    [boards.length],
  );

  // TableBody swaps its children for its own skeleton rows while dataState.isLoading, so this
  // only governs the accessible loading marker below.
  const showSkeleton = isLoading && renderRows.length === 0;

  return (
    <>
      {/* Table's typed children only accept TableHeader/TableBody, so this
          accessible status text lives outside it rather than as a cell. */}
      {showSkeleton ? (
        <span
          aria-label="Loading column values"
          role="status"
          style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}
        />
      ) : null}
      {/* The scope for the grid-template override. A wrapper rather than a className on Table
          itself: the custom property has to reach the rowgroups from ABOVE the element Vibe
          sets its inline value on, and Table forwards className to that same element. */}
      <div
        className={GRID_SCOPE_CLASS}
        style={{ "--discrepancy-grid-template": gridTemplate } as React.CSSProperties}
      >
      <Table
        columns={columns}
        errorState={<Text type="text2">Something went wrong loading these values.</Text>}
        emptyState={<Text type="text2">{emptyMessage}</Text>}
        dataState={{ isLoading }}
        withoutBorder
      >
        {/* One array literal for the same reason as `cells` in renderEntry —
            TableHeader's children type doesn't accept interleaved single
            elements and a mapped array as separate JSX children. */}
        <TableHeader>
          {[
            <TableHeaderCell key="label" title="Column" sticky />,
            ...boards.map((board) =>
              board.isResolving ? (
                <TableHeaderCell
                  key={board.boardId}
                  title={
                    <Skeleton
                      type="text"
                      size="small"
                      width={90}
                      data-testid="board-name-skeleton"
                    />
                  }
                />
              ) : (
                <TableHeaderCell
                  key={board.boardId}
                  // A ReactNode title rather than a plain string, so a long board name can
                  // truncate with its full text still reachable on hover. Mirrors the Text
                  // styling TableHeaderCell applies to a string title.
                  title={
                    <Tooltip content={board.boardName}>
                      <Text type="text2" weight="medium" color="secondary" ellipsis>
                        {board.boardName}
                      </Text>
                    </Tooltip>
                  }
                  // Renders as an Info icon + tooltip. Carries the "you might not have access"
                  // explainer for an unresolved board, which BoardsColumn used to show.
                  infoContent={board.infoContent}
                />
              ),
            ),
            <TableHeaderCell
              key="status"
              title="Status"
              className={STICKY_END_CLASS}
              data-testid="discrepancy-grid-status-header"
            />,
          ]}
        </TableHeader>

        {/* No hand-built skeleton body here: TableBody discards its children outright and
            renders its own skeleton rows whenever `dataState.isLoading` is true, so one
            would be unreachable code that merely looked live. */}
        {shouldVirtualize ? (
          // react-window (inside TableVirtualizedBody) measures its container via
          // react-virtualized-auto-sizer, which reads getBoundingClientRect on the
          // nearest DOM ancestor. TableVirtualizedBody itself takes no `style` prop,
          // so this wrapper gives that measurement something real to read from in a
          // browser that doesn't already constrain the table's height.
          <div style={{ height: virtualizedHeight }} data-testid="discrepancy-grid-virtualized-body">
            <TableVirtualizedBody
              items={renderRows}
              rowRenderer={(item) => renderEntry(item as unknown as RenderRow)}
            />
          </div>
        ) : (
          <TableBody>{renderRows.map(renderEntry)}</TableBody>
        )}
      </Table>
      </div>
    </>
  );
};

export default DiscrepancyGrid;
